import { Modal } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { InfoCircleFilled } from '@ant-design/icons';

import * as St from './ChipMinimizer.styles';
import { hasChipMinimizer } from './ChipMinimizer.utils';

import { IMAGE_COMING_SOON, NO_CHIP_MINIMIZER } from '~/core/constants';
import { ProductType } from '~/core/enums';
import { DrawingShape } from '~/core/models';
import { imgUrl } from '~/core/utils';
import { useChipMinimizerContext, useDrawingShapeContext } from '~contexts';
import DesignStepContainer from '~molecules/DesignStepContainer';
import ApplianceCard from '~organisms/ApplianceCard';

const ChipMinimizer = () => {
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showChipMinimizer, setShowChipMinimizer] = useState(false);
  const {
    getters: { drawingShapes },
  } = useDrawingShapeContext();
  const {
    chipMinimizer,
    setSelectedChipMinimizers,
    selectedChipMinimizers,
    resetSelectedChipMinimizers,
  } = useChipMinimizerContext();
  const { t } = useTranslation();

  const updateQty = (productId: number, value = 1) => {
    setSelectedChipMinimizers({
      ...selectedChipMinimizers,
      [productId]: {
        isSelected: value > 0,
        isManuallyChanged: false,
        quantity: Math.max(value, 0),
      },
    });
  };

  useEffect(() => {
    if (chipMinimizer) {
      const appliances = drawingShapes
        .flatMap((drawingShape: DrawingShape) => drawingShape.appliances)
        .map((drawingAppliance) => drawingAppliance.applianceModel);

      setShowChipMinimizer(appliances.some(hasChipMinimizer));
    }
  }, [drawingShapes, chipMinimizer, resetSelectedChipMinimizers]);

  useEffect(() => {
    if (chipMinimizer && !showChipMinimizer) {
      resetSelectedChipMinimizers();
    }
  }, [chipMinimizer, resetSelectedChipMinimizers, showChipMinimizer]);

  if (!chipMinimizer || !showChipMinimizer) {
    return null;
  }

  const chipMinimizerQuantity =
    selectedChipMinimizers[chipMinimizer.productId]?.quantity || 0;

  return (
    <>
      <p>
        <St.Title>{t('CHIP_MINIMIZER.TITLE')}</St.Title>
        <InfoCircleFilled
          className="ChipMinimizerSection__info-icon"
          onClick={() => setShowInfoModal(true)}
        />
      </p>
      <DesignStepContainer>
        <ApplianceCard
          applianceType={ProductType.CHIP_MINIMIZER}
          title={t('SCROLLABLE_AREA.SINK.MODIFY_CHIP_MINIMIZER')}
          image={imgUrl(chipMinimizer.imageUrl || IMAGE_COMING_SOON, 300)}
          priceWithoutDiscount={chipMinimizer.price}
          counterProps={{
            quantity: chipMinimizerQuantity,
            onIncrease: () => {
              updateQty(chipMinimizer.productId, chipMinimizerQuantity + 1);
            },
            onDecrease: () => {
              updateQty(chipMinimizer.productId, chipMinimizerQuantity - 1);
            },
          }}
        />
        <ApplianceCard
          applianceType={ProductType.CHIP_MINIMIZER}
          title={NO_CHIP_MINIMIZER.name}
          image={NO_CHIP_MINIMIZER.imageUrl}
          priceWithoutDiscount={0}
          counterProps={{
            quantity: selectedChipMinimizers[NO_CHIP_MINIMIZER.productId]
              ?.isSelected
              ? 1
              : 0,
            onIncrease: () => updateQty(NO_CHIP_MINIMIZER.productId),
            onDecrease: () => updateQty(NO_CHIP_MINIMIZER.productId),
          }}
        />
      </DesignStepContainer>
      <Modal
        open={showInfoModal}
        onCancel={() => setShowInfoModal(false)}
        width="500px"
        centered
        footer={null}
        closable
      >
        <span>{t('CHIP_MINIMIZER.INFO_CONTENT')}</span>
      </Modal>
    </>
  );
};

export default ChipMinimizer;
